import { api } from "../api_middleware";
import { makeErrorClass } from "../utils/customErrors";
import {
  getHttpUrl,
  getEffectiveEnvironment,
  getLocalEnvOverride,
  getForcePortOverride,
} from "../utils/serverUrls";
import { PixelStreamingExperience } from "./userEndpoints";
import {
  getCachedSupportedCodecsWithHardware,
  getHardwareCodecNames,
} from "../hooks/useDecoderCapabilities";

export enum ConnectionMethod {
  Qa = "qa",
  Production = "production",
  Demo = "demo",
  Local = "local",
  Test = "test",
  Staging = "staging",
  Cloud = "cloud",

  Localps = "localps",
  Pslocal = "pslocal",
  IsoStaging = "iso_staging",
  Direct = "direct",

  Advaith = "advaith",
  Andy2 = "andy2",
  Scott = "scott2",
  Scott2 = "scott2",
  Colin = "colin",
  Cesar = "cesar",
  Eli = "eli",
  Colm = "colm",
  Sharang = "sharang",
  Jake = "jake",
}

export enum NodeType {
  AZURE_VMSS_INSTANCE = "AZURE_VMSS_INSTANCE",
  MOCK_INSTANCE = "MOCK_INSTANCE",
  LAMBDA_LABS_DOCKER_INSTANCE = "LAMBDA_LABS_DOCKER_INSTANCE",
  RUNPOD_POD_INSTANCE = "RUNPOD_POD_INSTANCE",
  GAME_LIFT_STREAM_GROUP = "GAME_LIFT_STREAM_GROUP",
  AWS_ASG_INSTANCE = "AWS_ASG_INSTANCE",
}

export enum SessionStatus {
  Creating = "creating",
  ReadyToConfigure = "ready_to_configure",
  Configuring = "configuring",
  ReadyToStart = "ready_to_start",
  InProgress = "in_progress",
  Finished = "finished",
  Finalized = "finalized",
}

export enum Codec {
  AV1 = "AV1",
  H264 = "H264",
  VP9 = "VP9"
}

export interface SessionSchedulingConstraints {
  supported_node_types?: NodeType[];
  specific_node_id?: string;
  preferred_codecs?: Codec[] | null;
  launch_dedicated_phantom?: boolean;
  phantom_warmup_delay_seconds?: number;
  // force_new_node?: boolean;
  // required_node_type?: string;
}

export interface CreateSessionRequest {
  conversation_id?: string;
  organization_id?: string;
  connection_method_override?: ConnectionMethod;
  additional_scheduling_constraints?: SessionSchedulingConstraints;
  runs_on_test_infra: boolean;
  force_cold_launch?: boolean;
  create_token_jwt?: string;
  supports_200_with_error_data: boolean;
  res_link_params_id?: string;
  res_link_testing_organization_id?: string;
}

export interface APISession {
  session_id: string;
  deployment_id: string;
  organization_id: string;
  status: SessionStatus;
  conversation_id: string;
  connection_method: ConnectionMethod;
  build_id?: string;
  scheduling_constraints?: SessionSchedulingConstraints;
  created_at: string; // or Date if parsed, but string for raw JSON API
  expire_after?: string; // or Date
  internal_user_id?: string;
  runs_on_test_infra: boolean;
  pixel_streaming_experience?: PixelStreamingExperience;
  telemetry?: SessionTelemetry;  // Client-reported telemetry (with server-generated timestamps)
}

export enum CreateSessionErrorClass {
  existing_active_session = "existing_active_session",
  link_consumption_exhausted = "link_consumption_exhausted",
}

export interface CreateSessionErrorExistingActiveSession {
  session: APISession;
}

export interface CreateSessionErrorLinkConsumptionExhausted {
  res_link_params_id: string;
  error_text?: string | null;
}

export interface CreateSessionError {
  error_class: CreateSessionErrorClass;
  existing_active_session?: CreateSessionErrorExistingActiveSession;
  link_consumption_exhausted?: CreateSessionErrorLinkConsumptionExhausted;
}

export interface CreateSessionResponse {
  session?: APISession;
  error?: CreateSessionError;
}

export enum StartSessionFailureClass {
  NotQuiteReady = "NOT_QUITE_READY",
  SessionCancelled = "SESSION_CANCELLED",
}

export interface StartSessionFailure {
  failure_class: StartSessionFailureClass;
  expected_ready_after?: string | null;
}

export interface SessionStartResponse {
  session: APISession;
  stream_token: string | null;
  failure: StartSessionFailure | null;
}

export interface SessionEndResponse {
  session: APISession;
}

export interface ServiceConfig {
  environment: string;
  localDevConnectionMethod?: string;
  port?: number;
}

export interface SessionSpec {
  conversationId?: string;
  organizationId: string;
  sessionRemote?: string;
  puppetConfig: ServiceConfig;
  pixelsConfig: ServiceConfig;
  testInfra?: boolean;
  forceColdLaunch?: boolean;
  createTokenJwt?: string;
  resLinkParamsId?: string;
  preventEnvOverride?: boolean;
  resLinkTestingOrganizationId?: string;
  specificNodeId?: string;
  launchDedicatedPhantom?: boolean;
  phantomWarmupDelaySeconds?: number;
  // TODO: Backend not ready for force_new_node yet
  // forceNewNode?: boolean;
  // requiredNodeType?: string;
}

export interface EndOfSessionFeedbackRequest {
  session_id: string;
  rating_stars?: number;
  feedback_text?: string;
}

export interface EndOfSessionFeedbackResponse {
}

// Request DTOs (no timestamps) - used when reporting telemetry to server
export interface NegotiatedCodecDiagnosticRequest {
  codec: string;
  negotiation_duration_sec: number;  // Duration in seconds
}

export interface SessionTelemetryRequest {
  negotiated_codec: NegotiatedCodecDiagnosticRequest | null;
}

// Response/Storage models (with timestamps) - used in API responses
export interface NegotiatedCodecDiagnostic {
  codec: string;
  negotiation_duration_sec: number;  // Duration in seconds
  created_timestamp: string;  // ISO 8601 timestamp, server-generated
}

export interface SessionTelemetry {
  negotiated_codec: NegotiatedCodecDiagnostic | null;
}

export interface ReportSessionTelemetryRequest {
  session_id: string;
  telemetry: SessionTelemetryRequest;
}

export interface ReportSessionTelemetryResponse {
  session: APISession;
}

const getSessionRemoteOverride = () => import.meta.env.VITE_SESSION_REMOTE_OVERRIDE;

export function getSessionSpec(
  organizationId: string,
  conversationId?: string,
  createTokenJwt?: string,
  resLinkParamsId?: string
): SessionSpec {
  const env = getEffectiveEnvironment("PRODUCTION");
  const local = getLocalEnvOverride() ?? undefined;
  const forcePort = getForcePortOverride() ?? undefined;
  const sessionOverride = getSessionRemoteOverride() ?? undefined;
  const sessionSpec: SessionSpec = {
    conversationId: conversationId,
    organizationId: organizationId,
    sessionRemote: sessionOverride,
    puppetConfig: {
      environment: env,
      localDevConnectionMethod: local,
      port: forcePort,
    },
    pixelsConfig: {
      environment: env,
      localDevConnectionMethod: local,
      port: forcePort,
    },
    testInfra: false,
    createTokenJwt: createTokenJwt,
    resLinkParamsId: resLinkParamsId,
  };
  return sessionSpec;
}

export const CreateSessionException = makeErrorClass("CreateSessionException");
export const GetSessionError = makeErrorClass("GetSessionError");
export const GetActiveSessionError = makeErrorClass("GetActiveSessionError");
export const StartSessionError = makeErrorClass("StartSessionError");

const getHttpUrlFromSpec = (spec: SessionSpec, usePixelsConfig: boolean = true) => {
  const config = usePixelsConfig ? spec.pixelsConfig : spec.puppetConfig;
  return getHttpUrl(config.environment, config.localDevConnectionMethod, config.port, spec.preventEnvOverride);
};

export const getActiveConversationSession = async (spec: SessionSpec): Promise<APISession | undefined> => {
  if (!spec?.conversationId) {
    return undefined;
  }
  const baseHttpUrl = getHttpUrlFromSpec(spec);
  const getActiveSessionRequest = {
    conversation_id: spec.conversationId,
    runs_on_test_infra: spec.testInfra || false,
  };
  console.log("getActiveConversationSession:", baseHttpUrl, getActiveSessionRequest);
  const activeSessionResponse = await api(
    "v1/pixels/sessions/get-active-session-for-conversation",
    "POST",
    baseHttpUrl,
    getActiveSessionRequest
  );
  console.log("getActiveConversationSession response:", activeSessionResponse);
  if (!activeSessionResponse?.session) {
    return undefined;
  }
  const streamingSession = activeSessionResponse.session as APISession;
  console.log("getActiveConversationSession got session:", streamingSession);
  return streamingSession;
};

export const createSession = async (spec: SessionSpec): Promise<CreateSessionResponse> => {
  const baseHttpUrl = getHttpUrlFromSpec(spec);
  const codecs = getHardwareCodecNames(await getCachedSupportedCodecsWithHardware());
  const createSessionRequest: any = {
    conversation_id: spec.conversationId,
    organization_id: spec.organizationId,
    connection_method_override: spec.sessionRemote,
    runs_on_test_infra: spec.testInfra || false,
    force_cold_launch: spec.forceColdLaunch || false,
    create_token_jwt: spec.createTokenJwt,
    res_link_params_id: spec.resLinkParamsId,
    res_link_testing_organization_id: spec.resLinkTestingOrganizationId,
    supports_200_with_error_data: true,
  };
  createSessionRequest.additional_scheduling_constraints = {
    supported_node_types: [NodeType.AWS_ASG_INSTANCE],
    specific_node_id: spec.specificNodeId,
    preferred_codecs: codecs,
    launch_dedicated_phantom: spec.launchDedicatedPhantom,
    phantom_warmup_delay_seconds: spec.phantomWarmupDelaySeconds,
    // force_new_node: spec.forceNewNode,
    // required_node_type: spec.requiredNodeType,
  };
  const createSessionResponse = await api(
    "v1/pixels/sessions/create-session",
    "POST",
    baseHttpUrl,
    createSessionRequest
  );
  console.log("CreateSession response:", createSessionResponse);
  return createSessionResponse;
};

export const getSession = async (
  spec: SessionSpec,
  sessionId: string,
  performLogging: boolean = true
): Promise<APISession> => {
  const baseHttpUrl = getHttpUrlFromSpec(spec);
  const getSessionRequest = {
    session_id: sessionId,
  };
  const getSessionResponse = await api(
    "v1/pixels/sessions/get-session",
    "POST",
    baseHttpUrl,
    getSessionRequest,
    undefined,
    performLogging
  );
  if (!getSessionResponse?.session) {
    throw new GetSessionError("getSession: No session returned from server");
  }
  const streamingSession = getSessionResponse.session as APISession;
  if (performLogging) {
    console.log("getSession got session:", streamingSession);
  }
  return streamingSession;
};

export const startSession = async (
  spec: SessionSpec,
  sessionId: string
): Promise<SessionStartResponse> => {
  console.log("startSession starting:", spec, sessionId);
  const baseHttpUrl = getHttpUrlFromSpec(spec);
  const startSessionRequest = {
    session_id: sessionId,
    wait_for_sec: 20,
  };
  const startSessionResponse = await api(
    "v1/pixels/sessions/start-session",
    "POST",
    baseHttpUrl,
    startSessionRequest
  );
  console.log("startSession response:", startSessionResponse);
  return startSessionResponse as SessionStartResponse;
};

export const endSession = async (
  spec: SessionSpec,
  sessionId: string,
  keepalive: boolean = false
): Promise<SessionEndResponse> => {
  console.log("endSession starting:", spec, sessionId);
  const baseHttpUrl = getHttpUrlFromSpec(spec);
  const endSessionRequest = {
    session_id: sessionId,
  };
  const endSessionResponse = await api(
    "v1/pixels/sessions/end-session",
    "POST",
    baseHttpUrl,
    endSessionRequest,
    { keepalive: keepalive }
  );
  console.log("endSession response:", endSessionResponse);
  return endSessionResponse as SessionEndResponse;
};

export const getActiveUserSession = async (
  spec: SessionSpec,
  userId: string
): Promise<APISession | null> => {
  console.log("getActiveUserSession starting:", userId, spec);
  // Will we even have a spec? Unclear. This function is unused for now anyway: punt!
  // const baseUrl = getHttpUrl(
  //   getEffectiveEnvironment("PRODUCTION"),
  //   getLocalEnvOverride() ?? undefined,
  //   getForcePortOverride() ?? undefined,
  //   undefined
  // );
  const baseUrl = getHttpUrlFromSpec(spec);
  const userSessionRequest = { user_id: userId };
  const userSessionResponse = await api(
    "v1/pixels/sessions/get-active-session-for-user",
    "POST",
    baseUrl,
    userSessionRequest,
  );
  console.log("getActiveUserSession response:", userSessionResponse);
  return userSessionResponse.session as APISession;
}

export const sendEndOfSessionFeedback = async (spec: SessionSpec, sessionId: string, stars?: number, comment?: string): Promise<EndOfSessionFeedbackResponse> => {
  const sendFeedbackRequest: EndOfSessionFeedbackRequest = {
    session_id: sessionId,
    rating_stars: stars,
    feedback_text: comment,
  };
  const response = await api(
    "v1/pixels/sessions/end-of-session-feedback",
    "POST",
    getHttpUrlFromSpec(spec),
    sendFeedbackRequest
  );
  return response as EndOfSessionFeedbackResponse;
};

export const reportSessionTelemetry = async (
  spec: SessionSpec,
  sessionId: string,
  negotiatedCodec: string,
  negotiationDurationMs: number
): Promise<ReportSessionTelemetryResponse> => {
  // Convert milliseconds to seconds
  const durationSeconds = negotiationDurationMs / 1000;

  const request: ReportSessionTelemetryRequest = {
    session_id: sessionId,
    telemetry: {
      negotiated_codec: {
        codec: negotiatedCodec,
        negotiation_duration_sec: durationSeconds,
      },
    },
  };
  const response = await api(
    "v1/pixels/sessions/report-session-telemetry",
    "POST",
    getHttpUrlFromSpec(spec),
    request,
    undefined,
    false // Don't log the request
  );
  return response as ReportSessionTelemetryResponse;
};
